/**
 * Quantity-mode (MDOT-24700) "Convert to quantities" dialog: explains what converting a
 * weight-based portfolio into quantity mode entails and asks for the AUM budget the weights
 * are split against — with an AUM of 0 every derived quantity and monetary value would be 0
 * regardless of the weights, so the amount must be strictly positive. Spawned from the
 * PortfolioDetails Edit dropdown; confirming hands the validated fields to the caller's
 * `onSubmitAsync`, which owns the conversion itself.
 */
import { MonetaryInput } from "$root/components/monetary-input";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { formatCurrencyAmount } from "$root/utils/currency";
import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { investmentAum } from "$root/functional-areas/customAttributes/aum";
import { MAX_QUANTITY_AUM } from "./AumDialog";

type ConvertToQuantitiesDialogProps = {
	portfolioUid: string;
	/** Portfolio name shown in the explanation. */
	portfolioName?: string;
	/** Currency of the AUM input; the platform default when unset. */
	currency?: Currencies | null;
	/**
	 * Performs the conversion with the validated form fields; a rejection keeps the dialog
	 * open, a fulfillment closes it.
	 */
	onSubmitAsync(fields: { aum: number }): Promise<void>;
};

function ConvertToQuantitiesDialog({
	show,
	onClose,
	onAnimationStateChange,
	portfolioName,
	currency,
	defaultAum,
	onSubmitAsync,
}: Omit<ConvertToQuantitiesDialogProps, "portfolioUid"> & { defaultAum: number | null } & MinimumDialogProps) {
	const { control, formState, handleSubmit } = useForm<{ aum: number | null }>({
		defaultValues: { aum: defaultAum },
		resolver: zodResolver(
			z.object({
				aum: z
					.number({
						required_error: "Please provide the portfolio AUM",
						invalid_type_error: "Please provide the portfolio AUM",
					})
					.gt(0, "The portfolio AUM must be greater than zero")
					.lte(
						MAX_QUANTITY_AUM,
						`The portfolio AUM cannot exceed ${formatCurrencyAmount(MAX_QUANTITY_AUM, currency, 0)}`,
					),
			}),
		),
	});

	return (
		<Dialog
			show={show}
			onClose={onClose}
			header="Convert to quantities"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							Cancel
						</Button>
					}
					primaryAction={
						<SubmitButton data-qualifier="PortfolioDetails/ConvertToQuantitiesDialog/Confirm">Convert</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => handleSubmit(({ aum }) => onSubmitAsync({ aum: aum! }))()}
		>
			<Text type="Body/M/Book" className="mb-4" as="p">
				{portfolioName ? (
					<>
						The composition of <strong>{portfolioName}</strong> will be expressed as instrument quantities instead of
						weights.
					</>
				) : (
					<>The portfolio composition will be expressed as instrument quantities instead of weights.</>
				)}
			</Text>
			<Text type="Body/M/Book" className="mb-4" as="p">
				The current weights are converted to quantities using the latest instrument prices and the AUM below. The AUM
				becomes a fixed budget: weights are derived from the quantities you hold, and any unallocated amount is kept as
				cash.
			</Text>
			<FormFields.Custom control={control} formState={formState} name="aum" label="Portfolio AUM" className="mb-4">
				{({ id, invalid, field }) => (
					<MonetaryInput
						id={id}
						invalid={invalid}
						value={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						min={0}
						max={MAX_QUANTITY_AUM}
						currency={currency}
						data-qualifier="PortfolioDetails/ConvertToQuantitiesDialog/AumInput"
					/>
				)}
			</FormFields.Custom>
			<Text type="Body/M/Book" as="p">
				You can review and adjust the resulting quantities in the composition editor before saving.
			</Text>
		</Dialog>
	);
}

export async function spawnConvertToQuantitiesDialog(
	props: ConvertToQuantitiesDialogProps,
): Promise<SpawnResult<void>> {
	// Prefill with the portfolio's current AUM; a failed read just leaves the field empty.
	const defaultAum = await investmentAum.fetchQuery(props.portfolioUid, "INVESTMENT").catch(() => null);
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<ConvertToQuantitiesDialog
				{...props}
				defaultAum={defaultAum}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (fields) => {
					await props.onSubmitAsync(fields);
					resolve();
				}}
			/>
		)),
	);
}
